import { c as _c } from "react/compiler-runtime";
import type { CreateTenantRoleRequest, TenantRoleKindDiscriminant, TenantScope } from '@onefootprint/request-types/dashboard';
import { Form as FormComponent } from '@onefootprint/ui';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { tenantScopeFromVaultProxyOption } from '../../../hooks/use-vault-proxy-options';
import { getDecryptScopes } from '../../../utils/decrypt-access';
import { withAccess } from '../../../utils/scope-environments';
import Permissions from '../permissions';
import type { FormData } from './role-form.types';
type RoleFormProps = {
  defaultValues?: FormData;
  onSubmit: (payload: CreateTenantRoleRequest) => void;
  kind: TenantRoleKindDiscriminant;
  /**
   * When true (create flow), the admin can pick the role kind. The auditor entity-tags field shows
   * for any auditor role in both create and edit — tags live in the `entity_read` scope, which the
   * role-update path persists, so edits take effect.
   */
  allowKindSelection?: boolean;
};
const parseEntityTags = (raw: string): string[] => raw.split(',').map(tag => tag.trim()).filter(Boolean);
const RoleForm = t0 => {
  const $ = _c(44);
  const {
    onSubmit,
    defaultValues,
    kind,
    allowKindSelection: t1
  } = t0;
  const allowKindSelection = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "form"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("roles", t2);
  let t3;
  if ($[1] !== defaultValues || $[2] !== kind) {
    t3 = defaultValues ?? {
      name: "",
      access: {},
      decryptAccess: {},
      vaultProxyConfigs: [],
      kind,
      entityTags: ""
    };
    $[1] = defaultValues;
    $[2] = kind;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = {
      defaultValues: t3
    };
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const formMethods = useForm(t4);
  const {
    handleSubmit,
    control
  } = formMethods;
  const selectedKind = useWatch({
    control,
    name: "kind"
  }) ?? kind;
  const isAuditor = selectedKind === "auditor";
  let t5;
  if ($[6] !== isAuditor || $[7] !== onSubmit || $[8] !== selectedKind) {
    t5 = formData => {
      const {
        name,
        access,
        decryptAccess,
        vaultProxyConfigs,
        entityTags
      } = formData;
      const grantedPermissions = (Object.keys(access) as TenantScope['kind'][]).filter(permission => (access[permission] ?? "none") !== "none");
      const decryptScopes = getDecryptScopes(decryptAccess);
      if (isAuditor) {
        onSubmit({
          name,
          kind: selectedKind,
          scopes: [{
            kind: "entity_read",
            tags: parseEntityTags(entityTags)
          }, ...decryptScopes]
        });
        return;
      }
      const vaultProxyScopes = grantedPermissions.includes("invoke_vault_proxy") ? vaultProxyConfigs.map(_temp) : [];
      const tenantScopeKinds = grantedPermissions.filter(_temp2).map(_temp3);
      if (!tenantScopeKinds.some(_temp4)) {
        tenantScopeKinds.push({
          kind: "read"
        } as TenantScope);
      }
      const allScopes = [...withAccess(tenantScopeKinds, access), ...decryptScopes, ...vaultProxyScopes];
      onSubmit({
        name,
        scopes: allScopes,
        kind: selectedKind
      });
    };
    $[6] = isAuditor;
    $[7] = onSubmit;
    $[8] = selectedKind;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleAfterSubmit = t5;
  let t6;
  if ($[10] !== handleAfterSubmit || $[11] !== handleSubmit) {
    t6 = handleSubmit(handleAfterSubmit);
    $[10] = handleAfterSubmit;
    $[11] = handleSubmit;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t) {
    t7 = t(_temp5);
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t) {
    t8 = t(_temp6);
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t) {
    t9 = t(_temp7);
    $[17] = t;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t9) {
    t10 = {
      required: t9
    };
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t7 || $[23] !== t8) {
    t11 = <FormComponent.Input name="name" size="compact" label={t7} placeholder={t8} rules={t10} />;
    $[21] = t10;
    $[22] = t7;
    $[23] = t8;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== allowKindSelection || $[26] !== t) {
    t12 = allowKindSelection && <FormComponent.Select name="kind" size="compact" label={t(_temp8)} options={[{
      label: t(_temp9),
      value: "dashboard_user"
    }, {
      label: t(_temp10),
      value: "auditor"
    }]} />;
    $[25] = allowKindSelection;
    $[26] = t;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== isAuditor || $[29] !== t) {
    t13 = isAuditor && <FormComponent.Input name="entityTags" size="compact" label={t(_temp11)} placeholder={t(_temp12)} hint={t(_temp13)} rules={{
      validate: value => parseEntityTags(typeof value === "string" ? value : "").length > 0 || t(_temp14)
    }} />;
    $[28] = isAuditor;
    $[29] = t;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== selectedKind) {
    t14 = <Permissions kind={selectedKind} />;
    $[31] = selectedKind;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t11 || $[34] !== t12 || $[35] !== t13 || $[36] !== t14) {
    t15 = <div className="flex flex-col gap-6">{t11}{t12}{t13}{t14}</div>;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t15 || $[39] !== t6) {
    t16 = <form id="roles-form" onSubmit={t6} className="w-full">{t15}</form>;
    $[38] = t15;
    $[39] = t6;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== formMethods || $[42] !== t16) {
    t17 = <FormProvider {...formMethods}>{t16}</FormProvider>;
    $[41] = formMethods;
    $[42] = t16;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  return t17;
};
export type { RoleFormProps };
export default RoleForm;
function _temp(opt) {
  return tenantScopeFromVaultProxyOption(opt);
}
function _temp2(permission_0) {
  return permission_0 !== "decrypt" && permission_0 !== "invoke_vault_proxy";
}
function _temp3(s) {
  return {
    kind: s
  } as TenantScope;
}
function _temp4(s_0) {
  return s_0.kind === "read";
}
function _temp5($) {
  return $.name.label;
}
function _temp6($_0) {
  return $_0.name.placeholder;
}
function _temp7($_1) {
  return $_1.name.errors.required;
}
function _temp8($_2) {
  return $_2.kind.label;
}
function _temp9($_3) {
  return $_3.kind.options.dashboardUser;
}
function _temp10($_4) {
  return $_4.kind.options.auditor;
}
function _temp11($_5) {
  return $_5.entityTags.label;
}
function _temp12($_6) {
  return $_6.entityTags.placeholder;
}
function _temp13($_7) {
  return $_7.entityTags.hint;
}
function _temp14($_8) {
  return $_8.entityTags.errors.required;
}